import {inject, Injectable} from '@angular/core';
import {
  collection,
  collectionChanges,
  collectionData,
  doc,
  DocumentData,
  Firestore,
  getDoc,
  getDocs,
  query,
  QueryDocumentSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from '@angular/fire/firestore';
import {forkJoin, from, Observable, of, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class CoreService {

  serverTimestamp = serverTimestamp;
  protected firestore: Firestore;

  constructor(firestore?: Firestore) {
    this.firestore = firestore || inject(Firestore);
  }

  //#region COUNT DOCUMENTS
  /**
   * Retrieves the count of documents where a field matches any value in the provided array, as an observable.
   * @template DataType - The type of the document data.
   * @param path - The Firestore collection path.
   * @param fieldName - The field to filter by.
   * @param values - The array of values to match.
   * @returns An observable that emits the count of matching documents.
   */
  getCountDocumentsWhereFieldInValuesAsObservable<DataType>(
    path: string,
    fieldName: string,
    values: string[],
  ): Observable<number> {
    const q = query(
      collection(this.firestore, path),
      where(fieldName, 'in', values),
    );

    return from(
      getDocs(q).then(querySnapshot => {
        return querySnapshot.size;
      }),
    );
  }

  /**
   * Retrieves the count of documents where a field matches any value in the provided array,
   * and updates the count reactively as an observable.
   * @template DataType - The type of the document data.
   * @param path - The Firestore collection path.
   * @param fieldName - The field to filter by.
   * @param values - The array of values to match.
   * @returns An observable that emits the count of matching documents.
   */
  getCountDocumentsWhereFieldInValuesAsObservableValueChanges<DataType>(
    path: string,
    fieldName: string,
    values: string[],
  ): Observable<number> {
    const q = query(
      collection(this.firestore, path),
      where(fieldName, 'in', values),
    );
    return collectionChanges(q).pipe(
      map(actions => actions.length),
    );
  }

  /**
   * Retrieves the count of documents where a field matches any value in the provided array, as a promise.
   * @template DataType - The type of the document data.
   * @param path - The Firestore collection path.
   * @param fieldName - The field to filter by.
   * @param values - The array of values to match.
   * @returns A promise that resolves to the count of matching documents.
   */
  async countDocumentsWhereFieldInValuesAsPromise<DataType>(
    path: string,
    fieldName: string,
    values: string[],
  ): Promise<number> {
    const q = query(
      collection(this.firestore, path),
      where(fieldName, 'in', values),
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.size;
  }

  //#endregion

  //#region GET COLLECTION
  /**
   * Retrieves all documents from a Firestore collection as an observable.
   * @template DataType - The type of the document data.
   * @param path - The Firestore collection path.
   * @returns An observable that emits an array of documents.
   */
  getCollectionAsObservable<DataType>(path: string): Observable<DataType[]> {
    const collectionRef = collection(this.firestore, path);
    return from(
      getDocs(collectionRef).then(querySnapshot => {
        return querySnapshot.docs.map((doc: QueryDocumentSnapshot) => doc.data() as DataType);
      }),
    );
  }

  /**
   * Retrieves all documents from a Firestore collection as a promise.
   * @template DataType - The type of the document data.
   * @param path - The Firestore collection path.
   * @returns A promise that resolves to an array of documents.
   */
  async getCollectionAsPromise<DataType>(path: string): Promise<DataType[]> {
    const collectionRef = collection(this.firestore, path);
    const querySnapshot = await getDocs(collectionRef);
    return querySnapshot.docs.map((doc: QueryDocumentSnapshot) => doc.data() as DataType);
  }

  /**
   * Retrieves documents where a field matches any value in the provided array, as an observable.
   * @template DataType - The type of the document data.
   * @param path - The Firestore collection path.
   * @param fieldName - The field to filter by.
   * @param values - The array of values to match.
   * @returns An observable that emits an array of matching documents.
   */
  getCollectionByFieldInAsValuesAsObservable<DataType>(
    path: string,
    fieldName: string,
    values: string[],
  ): Observable<DataType[]> {
    const q = query(
      collection(this.firestore, path),
      where(fieldName, 'in', values),
    );
    return from(getDocs(q)).pipe(
      map(querySnapshot => {
        return querySnapshot.docs.map((doc: QueryDocumentSnapshot) => doc.data() as DataType);
      }),
    );
  }

  /**
   * Retrieves documents where a field matches any value in the provided array,
   * and updates reactively as an observable.
   * @template DataType - The type of the document data.
   * @param path - The Firestore collection path.
   * @param fieldName - The field to filter by.
   * @param values - The array of values to match.
   * @returns An observable that emits an array of matching documents.
   */
  getCollectionByFieldInValuesAsObservableValueChanges<DataType>(
    path: string,
    fieldName: string,
    values: string[],
  ): Observable<DataType[]> {
    const q = query(
      collection(this.firestore, path),
      where(fieldName, 'in', values),
    );
    return collectionData(q) as Observable<DataType[]>;
  }

  /**
   * Retrieves documents where a field matches any value in the provided array, as a promise.
   * @template DataType - The type of the document data.
   * @param path - The Firestore collection path.
   * @param fieldName - The field to filter by.
   * @param values - The array of values to match.
   * @returns A promise that resolves to an array of matching documents.
   */
  async getCollectionByFieldInValuesAsPromise<DataType>(
    path: string,
    fieldName: string,
    values: string[],
  ): Promise<DataType[]> {
    const q = query(
      collection(this.firestore, path),
      where(fieldName, 'in', values),
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc: QueryDocumentSnapshot) => doc.data() as DataType);
  }

  //#endregion

  //#region GET DOCUMENT
  /**
   * Retrieves a Firestore document as an observable.
   * @template DataType - The type of the document data.
   * @param path - The Firestore document path.
   * @returns An observable that emits the document data or undefined if not found.
   */
  getDocumentAsObservable<DataType>(path: string): Observable<DataType | undefined> {
    const docRef = doc(this.firestore, path);
    return from(
      getDoc(docRef).then(docSnapshot => {
        if (docSnapshot.exists()) {
          return docSnapshot.data() as DataType;
        } else {
          return undefined;
        }
      }),
    );
  }

  /**
   * Retrieves a Firestore document as a promise.
   * @template DataType - The type of the document data.
   * @param path - The Firestore document path.
   * @returns A promise that resolves to the document data or undefined if not found.
   */
  async getDocumentAsPromise<DataType>(
    path: string,
  ): Promise<DataType> {
    try {
      const docRef = doc(this.firestore, path);
      const docSnapshot = await getDoc(docRef);
      if (docSnapshot.exists()) {
        return docSnapshot.data() as DataType;
      } else {
        return undefined;
      }
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Fetches a single document from a collection where a field equals the given value.
   *
   * @template DataType - Type of the expected document
   * @param path - The path to the Firestore collection
   * @param field - The field name to filter on
   * @param value - The value the field should match
   * @returns Observable<DataType | undefined>
   */
  getDocumentByFieldAsObservable<DataType>(
    path: string,
    field: string,
    value: string,
  ): Observable<(DataType & {documentId: string})[]> {
    const q = query(
      collection(this.firestore, path),
      where(field, '==', value),
    );
    return collectionChanges(q).pipe(
      map(actions =>
        actions.map(a => {
          const data = a.doc.data() as DataType;
          const documentId: string = a.doc.id;
          return {documentId, ...data};
        }),
      ),
    );
  }

  /**
   * Retrieves multiple documents from a Firestore collection using their IDs.
   * Uses `getDocumentAsObservable` for each ID and combines them into a single observable.
   * Each document's ID is added to the document data under the specified property name.
   *
   * @template DataType - The type of the document data.
   * @param path - The Firestore collection path.
   * @param ids - An array of document IDs to fetch from the collection.
   * @param idProperty - The name of the property to store the document ID within the document data.
   * @returns An observable that emits an array of documents with the ID added to the data.
   */
  getMultipleDocumentsByIdsAsObservable<DataType>(
    path: string,
    ids: string[],
    idProperty: string,
  ): Observable<DataType[]> {
    if (!ids?.length || ids[0] === 'admin') {
      return of([] as DataType[]);
    }
    const documentObservables = ids?.map((id: string) =>
      this.getDocumentAsObservable<DataType>(`${path}/${id}`).pipe(
        map(docData => {
          if (docData) {
            docData[idProperty] = id;
          }
          return docData;
        }),
        catchError(error => {
          console.error('Error fetching document:', error);
          return from([undefined]);
        }),
      ),
    );

    return forkJoin(documentObservables).pipe(
      map(results => results.filter(doc => doc !== undefined) as DataType[]),
    );
  }

  //#endregion

  //#region UPDATE DOCUMENT
  /**
   * Updates a document in Firestore and returns an Observable that emits once the update is complete.
   *
   * Supports both update (requires document to exist) and merge (can create or merge fields).
   *
   * @template DataType - The type of data stored in the document.
   * @param {string} path - The Firestore document path.
   * @param {Partial<DataType>} data - The partial data to update or set on the document.
   * @param {boolean} [merge=false] - If true, uses `set` with merge to combine with existing data;
   *                                  otherwise uses `update`, which fails if the document doesn't exist.
   * @returns {Observable<void>} - An observable that emits when the update or merge operation is successful.
   */
  updateDocumentAsObservable<DataType>(
    path: string,
    data: Partial<DataType>,
    merge: boolean = false,
  ): Observable<void> {
    const docRef = doc(this.firestore, path);
    return from(
      merge ? setDoc(docRef, data as DataType, {merge: true}) : updateDoc(docRef, data as Partial<DocumentData>),
    );
  }

  /**
   * Updates a document in Firestore and returns a Promise that resolves once the update is complete.
   *
   * @template DataType - The type of data stored in the document.
   * @param {string} path - The Firestore document path.
   * @param {Partial<DataType>} data - The partial data to update the document with.
   * @returns {Promise<void>} - A promise that resolves when the update is successful.
   */
  async updateDocumentAsPromise<DataType>(path: string, data: Partial<DataType>): Promise<void> {
    const docRef = doc(this.firestore, path);
    try {
      await updateDoc(docRef, data as Partial<DocumentData>);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Updates multiple documents in Firestore within a batch operation.
   *
   * This method allows for the updating of multiple documents in Firestore in a single batch operation.
   * It accepts an array of updates, each consisting of a document path and the data to be updated.
   * The `merge` flag indicates whether to merge the new data with the existing document data
   * or overwrite the document entirely.
   *
   * @template DataType - The type of data stored in the document.
   * @param {Array<{ path: string; data: Partial<DataType> }>} updates - An array of update operations,
   *        where each operation consists of a `path` to the document and the `data` to update.
   * @param {boolean} [merge=false] - A flag that determines whether to merge the update data
   *        with the existing document data (`true`), or overwrite it completely (`false`).
   *
   * @returns {Observable<void>} An observable that resolves when the batch operation has completed.
   *
   * @throws {Error} If an error occurs during the batch operation, an error is thrown.
   */
  updateDocumentsAsObservable<DataType>(
    updates: {path: string; data: Partial<DataType>}[],
    merge: boolean = false,
  ): Observable<void> {
    const batch = writeBatch(this.firestore);
    updates.forEach(({path, data}) => {
      const docRef = doc(this.firestore, path);
      if (merge) {
        batch.set(docRef, data as Partial<DocumentData>, {merge: true});
      } else {
        batch.update(docRef, data as Partial<DocumentData>);
      }
    });
    return from(batch.commit()).pipe(
      catchError(error => {
        console.error('Error updating documents:', error);
        return throwError(error);
      }),
    );
  }

  //#endregion

}
